import { Button } from "@excalidraw-ms/shared-ui";

export type DrawingLoadErrorViewProps = {
  invalidScene: boolean;
  message: string;
  diagnostic?: string;
  copyDetailsError?: string;
  canDownloadOriginal: boolean;
  canRepairOrder: boolean;
  onBack: () => void;
  onCopyAiRepairPrompt: () => Promise<void>;
  onExportForRepair: () => void;
  onOpenRepairedCopy: () => void;
  onRetry: () => void;
};

export default function DrawingLoadErrorView({
  invalidScene,
  message,
  diagnostic,
  copyDetailsError,
  canDownloadOriginal,
  canRepairOrder,
  onBack,
  onCopyAiRepairPrompt,
  onExportForRepair,
  onOpenRepairedCopy,
  onRetry,
}: DrawingLoadErrorViewProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100vw",
        height: "100vh",
        fontFamily: "system-ui, sans-serif",
        fontSize: 18,
      }}
    >
      <div
        role="alert"
        style={{
          width: "min(560px, calc(100vw - 112px))",
          padding: 32,
          border: "1px solid var(--dialog-border-color)",
          borderRadius: 12,
          background: "var(--island-bg-color)",
          boxShadow: "var(--exms-shadow-modal)",
          textAlign: "left",
        }}
      >
        <h1 style={{ margin: "0 0 12px", fontSize: 26, lineHeight: 1.2 }}>
          We couldn't open this drawing
        </h1>
        <p style={{ margin: 0, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>
          {invalidScene ? "The drawing contains data Excalidraw can’t open." : message}
        </p>
        {invalidScene && (
          <p
            style={{
              margin: "12px 0 0",
              color: "var(--color-text-secondary)",
              lineHeight: 1.5,
            }}
          >
            {canRepairOrder
              ? "Excalidraw can try to repair it, but some damage may still prevent it from opening. The attempt does not save immediately."
              : canDownloadOriginal
                ? "Copy an AI repair prompt to ask an assistant connected to Excalidraw-MS MCP to diagnose and repair the drawing. You can also export the file for manual repair."
                : "Copy an AI repair prompt to ask an assistant connected to Excalidraw-MS MCP to diagnose the drawing and help recreate it."}
          </p>
        )}
        {copyDetailsError && (
          <p
            role="alert"
            style={{
              margin: "12px 0 0",
              color: "var(--color-danger)",
              lineHeight: 1.5,
            }}
          >
            {copyDetailsError}
          </p>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 10,
            marginTop: 24,
          }}
        >
          <Button variant="secondary" onClick={onBack}>
            Back to Home
          </Button>
          {invalidScene && diagnostic && (
            <Button variant="secondary" onClick={() => void onCopyAiRepairPrompt()}>
              Copy AI repair prompt
            </Button>
          )}
          {invalidScene && canDownloadOriginal && !canRepairOrder && (
            <Button variant="secondary" onClick={onExportForRepair}>
              Export for repair
            </Button>
          )}
          {canRepairOrder ? (
            <Button variant="primary" onClick={onOpenRepairedCopy}>
              Try repair and open
            </Button>
          ) : (
            !invalidScene && (
              <Button variant="primary" onClick={onRetry}>
                Try again
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
